// src/components/Dashboard_ADM/Atendimento/ConfiguracoesTab.tsx
// Camada editável por agente (tom, nome, saudação, encerramento, provedor de IA) — seção 8.6
// GET /whatsapp-agent/config | PATCH /whatsapp-agent/config/:agentId

import React, { useState, useEffect, useCallback } from 'react';
import { Settings2, Loader2, Save, CheckCircle, Mic } from 'lucide-react';
import apiClient from '../../../services/api';
import { AgentEditableConfig, ALL_AGENT_IDS, AGENT_LABELS } from './shared';

const ConfiguracoesTab: React.FC = () => {
  const [configs, setConfigs] = useState<Record<string, AgentEditableConfig>>({});
  const [loading, setLoading] = useState(true);
  const [salvandoId, setSalvandoId] = useState<string | null>(null);
  const [salvoId, setSalvoId] = useState<string | null>(null);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get<AgentEditableConfig[]>('/whatsapp-agent/config');
      const map: Record<string, AgentEditableConfig> = {};
      for (const cfg of data ?? []) map[cfg.agentId] = cfg;
      setConfigs(map);
    } catch {
      setConfigs({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const updateLocal = (agentId: string, patch: Partial<AgentEditableConfig>) => {
    setConfigs((prev) => ({
      ...prev,
      [agentId]: { ...prev[agentId], agentId: agentId as any, ...patch },
    }));
  };

  const handleSalvar = async (agentId: string) => {
    setSalvandoId(agentId);
    try {
      const cfg = configs[agentId];
      await apiClient.patch(`/whatsapp-agent/config/${agentId}`, {
        nomeAgente: cfg.nomeAgente,
        tom: cfg.tom,
        saudacaoCustom: cfg.saudacaoCustom || undefined,
        fraseEncerramentoCustom: cfg.fraseEncerramentoCustom || undefined,
        llmProvider: cfg.llmProvider || undefined,
        respondWithVoice: cfg.respondWithVoice ?? false,
      });
      setSalvoId(agentId);
      setTimeout(() => setSalvoId((id) => (id === agentId ? null : id)), 2500);
    } catch {
      alert('Erro ao salvar configuração');
    } finally {
      setSalvandoId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h2 className="text-2xl font-black text-gray-900 mb-1 flex items-center gap-3">
          <Settings2 className="w-6 h-6 text-blue-600" /> Configurações dos Agentes
        </h2>
        <p className="text-gray-600">
          Camada editável (tom, nome, saudação, encerramento, provedor de IA) — aplicada sem deploy.
          As regras fixas de segurança e escopo não são editáveis por aqui.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 text-blue-600 animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {ALL_AGENT_IDS.map((agentId) => {
            const cfg = configs[agentId] ?? { agentId, nomeAgente: 'Beatriz', tom: 'descontraido' as const };
            return (
              <div key={agentId} className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 rounded-full text-sm font-black bg-indigo-100 text-indigo-700">
                    {AGENT_LABELS[agentId]}
                  </span>
                  {salvoId === agentId && (
                    <span className="flex items-center gap-1 text-green-600 text-xs font-bold">
                      <CheckCircle className="w-4 h-4" /> Salvo
                    </span>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Nome do agente</label>
                    <input value={cfg.nomeAgente}
                      onChange={(e) => updateLocal(agentId, { nomeAgente: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-gray-900 font-semibold text-sm" />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Tom de voz</label>
                    <select value={cfg.tom}
                      onChange={(e) => updateLocal(agentId, { tom: e.target.value as 'formal' | 'descontraido' })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-gray-900 font-semibold text-sm">
                      <option value="descontraido">Descontraído e próximo</option>
                      <option value="formal">Formal e respeitoso</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Saudação personalizada (opcional)</label>
                    <textarea value={cfg.saudacaoCustom ?? ''} rows={2}
                      onChange={(e) => updateLocal(agentId, { saudacaoCustom: e.target.value })}
                      placeholder="Deixe em branco para usar a saudação padrão"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-gray-900 text-sm resize-none" />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Frase de encerramento (opcional)</label>
                    <textarea value={cfg.fraseEncerramentoCustom ?? ''} rows={2}
                      onChange={(e) => updateLocal(agentId, { fraseEncerramentoCustom: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-gray-900 text-sm resize-none" />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Provedor de IA</label>
                    <select value={cfg.llmProvider ?? ''}
                      onChange={(e) => updateLocal(agentId, { llmProvider: (e.target.value || undefined) as any })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-gray-900 font-semibold text-sm">
                      <option value="">Padrão (Groq → GPT → GLM)</option>
                      <option value="groq">Groq</option>
                      <option value="openai">GPT (OpenAI)</option>
                      <option value="glm">GLM-5.2</option>
                    </select>
                  </div>

                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={cfg.respondWithVoice ?? false}
                      onChange={(e) => updateLocal(agentId, { respondWithVoice: e.target.checked })}
                      className="w-4 h-4" />
                    <Mic className="w-4 h-4 text-gray-500" />
                    Também responder em áudio (voz clonada MiniMax)
                  </label>

                  <button onClick={() => handleSalvar(agentId)} disabled={salvandoId === agentId}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-50 text-sm">
                    {salvandoId === agentId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConfiguracoesTab;
